"use client";

import Image from "next/image";
import Link from "next/link";
import { useState, type ElementType } from "react";
import {
  ArrowRight,
  Atom,
  Brain,
  Rocket,
  Shield,
  Cpu,
  Bot,
  Network,
  Radar,
  Sparkles,
  Workflow,
  Cloud,
  Code,
  Gauge,
  GitBranch,
  Server,
  Zap,
  Database,
  CheckCircle2,
  Terminal,
} from "lucide-react";
import { BaseLayout } from "@/components/layout/BaseLayout";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import { useContentSection } from "@/hooks/useContent";
import type { Homepage } from "@/types";

const heroIconMap = {
  atom: Atom,
  bot: Bot,
  brain: Brain,
  cpu: Cpu,
  radar: Radar,
  rocket: Rocket,
  shield: Shield,
  network: Network,
} satisfies Record<string, ElementType>;

function Hero() {
  const heroData = useContentSection("homepage.hero") as Homepage["hero"];
  const [codeDisplay, setCodeDisplay] = useState('mlir');

  return (
    <header className="section-primary pt-2 pb-20">
      {/* Animated background pattern */}
      <div className="absolute inset-0 opacity-10 pointer-events-none">
        <div className="absolute inset-0 bg-[linear-gradient(rgba(99,102,241,0.1)_1px,transparent_1px),linear-gradient(90deg,rgba(99,102,241,0.1)_1px,transparent_1px)] bg-[size:50px_50px]"></div>
      </div>

      {/* Gradient orbs */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-purple-500/10 rounded-full filter blur-3xl"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-primary-500/10 rounded-full filter blur-3xl"></div>
      </div>

      <div className="relative">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-primary-500/20 to-purple-500/20 border border-primary-500/30 rounded-full mb-4">
            <Cpu className="w-4 h-4 text-primary-600 animate-pulse" />
            <span className="text-sm font-semibold bg-gradient-to-r from-primary-600 to-purple-600 bg-clip-text text-transparent">
              COMPLETE AI TECHNOLOGY STACK
            </span>
          </div>

          <h1 className="title-size font-bold mb-6 text-bg">
            <span className="block">Enterprise AI Solutions</span>
            <span className="block bg-gradient-to-r from-primary-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
              Silicon-to-Software
            </span>
          </h1>

          <p className="text-xl text-muted-foreground mb-8 max-w-4xl mx-auto leading-relaxed">
            Deep GPU & compiler expertise, advanced reasoning models, autonomous security, and production-ready infrastructure—delivering the complete AI stack for enterprise deployment.
          </p>

        </div>

        <section className="flex flex-col-reverse lg:grid lg:grid-cols-2 gap-12 items-start">
          {/* Left column: Core capabilities */}
          <article className="w-full space-y-8">
            <div className="space-y-6 text-[15px] lg:text-lg text-muted-foreground">
              {[
                {
                  icon: "cpu",
                  title: "Custom Kernel Development",
                  description: "CUDA, HIP, Triton expertise with FlashAttention variants and custom kernels development. MLIR pass development and Hardware-specific optimization"
                },
                {
                  icon: "brain",
                  title: "In-house development of Advanced Reasoning Models",
                  description: "e.g. Datarus-R1: a 14B parameter model with trajectory learning and demonstrated benchmark efficiency"
                },
                {
                  icon: "network",
                  title: "Production HPC Infrastructure",
                  description: "Multi-GPU clusters with InfiniBand/RoCE optimization, Kubernetes orchestration, and monitoring"
                },
                {
                  icon: "shield",
                  title: "Autonomous Security",
                  description: "24/7 AI-powered SOC with real-time threat detection, MITRE ATT&CK mapping, and automated response"
                }
              ].map((feature, index) => {
                const IconComponent = heroIconMap[feature.icon as keyof typeof heroIconMap];
                return (
                  <div key={index} className="flex gap-3 group">
                    <span className="iconBudg size-10 lg:size-12 mt-1 flex-shrink-0 group-hover:scale-110 transition-transform">
                      <IconComponent className="size-6 lg:size-7" />
                    </span>
                    <p className="flex flex-col text-left">
                      <span className="font-semibold text-bg text-lg">{feature.title}</span>
                      <span className="text-base">{feature.description}</span>
                    </p>
                  </div>
                );
              })}
            </div>

            <div className="flex flex-col sm:flex-row sm:justify-start gap-3 pt-4">
              {heroData.buttons.map((button, index) => (
                <Link key={index} href={button.link} target={button.target || "_self"}>
                  <Button
                    variant={button.variant}
                    size={button.size as "xl"}
                    className={cn(
                      "w-full sm:w-auto",
                      button.variant === "outline" ? "text-black group" : ""
                    )}
                  >
                    {button.icon ? (
                      <Image src={button.icon} alt={button.text} width={20} height={20} className="size-4" />
                    ) : null}
                    {button.text}
                    {button.variant === "outline" ? (
                      <ArrowRight className="size-4 group-hover:translate-x-1 transition-transform duration-300" />
                    ) : null}
                  </Button>
                </Link>
              ))}
            </div>
          </article>

          {/* Right column: Interactive code showcase */}
          <div className="relative w-full">
            <div className="absolute inset-0 bg-gradient-to-r from-primary-600 to-purple-600 rounded-3xl blur-3xl opacity-20 pointer-events-none"></div>
            <div className="relative bg-slate-950 rounded-3xl border-2 border-primary-200/30 shadow-2xl overflow-hidden">
              {/* Tab headers */}
              <div className="flex border-b border-slate-800 bg-slate-900">
                <button
                  onClick={() => setCodeDisplay('mlir')}
                  className={cn(
                    "flex-1 px-4 py-3 text-sm font-semibold transition-all flex items-center justify-center gap-2",
                    codeDisplay === 'mlir' 
                      ? 'bg-primary-600/20 text-primary-400 border-b-2 border-primary-400' 
                      : 'text-gray-500 hover:text-gray-300 hover:bg-slate-800'
                  )}
                >
                  <Code className="w-4 h-4" />
                  MLIR Optimization
                </button>
                <button
                  onClick={() => setCodeDisplay('triton')}
                  className={cn(
                    "flex-1 px-4 py-3 text-sm font-semibold transition-all flex items-center justify-center gap-2",
                    codeDisplay === 'triton' 
                      ? 'bg-purple-600/20 text-purple-400 border-b-2 border-purple-400' 
                      : 'text-gray-500 hover:text-gray-300 hover:bg-slate-800'
                  )}
                >
                  <Terminal className="w-4 h-4" />
                  Triton Kernel
                </button>
              </div>

              {/* Code display - left aligned like IDE */}
              <div className="p-6 bg-slate-950 min-h-[420px] text-left overflow-x-auto">
                {codeDisplay === 'mlir' ? (
                  <div className="space-y-2 font-mono text-large">
                      <div className="text-gray-500">{'// MLIR Custom Pass for AI Inference'}</div>
                    <div className="text-gray-400">
                      <span className="text-blue-400">func.func</span> <span className="text-green-400">@optimized_attention</span>(
                    </div>
                    <div className="pl-4 text-gray-400">
                      <span className="text-orange-400">%query</span>: tensor&lt;1024x768xf16&gt;,
                    </div>
                    <div className="pl-4 text-gray-400">
                      <span className="text-orange-400">%key</span>: tensor&lt;1024x768xf16&gt;
                    </div>
                    <div className="text-gray-400">) -&gt; tensor&lt;1024x1024xf16&gt; {`{`}</div>
                    <div className="pl-4 text-gray-400">
                        <span className="text-purple-400">%0</span> = <span className="text-cyan-400">&quot;gpu.matmul&quot;</span>(<span className="text-orange-400">%query, %key</span>)
                    </div>
                    <div className="pl-6 text-gray-400">
                      {`{`}<span className="text-yellow-400">transpose_b</span> = true,
                    </div>
                    <div className="pl-7 text-gray-400">
                        <span className="text-yellow-400">precision</span> = <span className="text-green-400">&quot;TF32&quot;</span>,
                    </div>
                    <div className="pl-7 text-gray-400">
                        <span className="text-yellow-400">fusion</span> = <span className="text-green-400">&quot;flash_attention&quot;</span>{`}`}
                    </div>
                    <div className="pl-4 text-gray-400">
                      <span className="text-blue-400">return</span> <span className="text-purple-400">%0</span> : tensor&lt;1024x1024xf16&gt;
                    </div>
                    <div className="text-gray-400">{`}`}</div>
                    
                    <div className="mt-4 p-4 bg-gradient-to-r from-green-500/20 to-emerald-500/20 rounded-xl border border-green-500/40">
                      <div className="flex items-center gap-2 text-green-300 text-xs font-semibold">
                        <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
                        Custom kernel fusion • notable speedups • significant memory savings
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2 font-mono text-sm">
                      <div className="text-gray-500">{'// Triton GPU Kernel for AI Training'}</div>
                    <div className="text-gray-400">
                      <span className="text-purple-400">@triton.jit</span>
                    </div>
                    <div className="text-gray-400">
                      <span className="text-blue-400">def</span> <span className="text-green-400">flash_attention_kernel</span>(
                    </div>
                    <div className="pl-4 text-gray-400">Q_ptr, K_ptr, V_ptr, Out_ptr,</div>
                    <div className="pl-4 text-gray-400">seq_len, head_dim,</div>
                    <div className="pl-4 text-gray-400">BLOCK_M: tl.constexpr,</div>
                    <div className="pl-4 text-gray-400">BLOCK_N: tl.constexpr</div>
                    <div className="text-gray-400">):</div>
                    <div className="pl-4 text-gray-400">
                      <span className="text-cyan-400">pid</span> = tl.program_id(<span className="text-orange-400">0</span>)
                    </div>
                    <div className="pl-4 text-gray-400">
                      <span className="text-cyan-400">offs_m</span> = pid * BLOCK_M + tl.arange(<span className="text-orange-400">0</span>, BLOCK_M)
                    </div>
                    <div className="pl-4 text-gray-400">
                      <span className="text-gray-500"># Fused attention computation</span>
                    </div>
                    <div className="pl-4 text-gray-400">
                      <span className="text-cyan-400">qk</span> = tl.dot(q, k, allow_tf32=<span className="text-green-400">True</span>)
                    </div>
                    <div className="pl-4 text-gray-400">
                      <span className="text-cyan-400">att</span> = tl.softmax(qk * scale)
                    </div>
                    <div className="pl-4 text-gray-400">
                      <span className="text-cyan-400">out</span> = tl.dot(att, v)
                    </div>
                    <div className="pl-4 text-gray-400">tl.store(Out_ptr + offs_m, out)</div>
                    
                    <div className="mt-4 p-4 bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-xl border border-purple-500/40">
                      <div className="flex items-center gap-2 text-purple-300 text-xs font-semibold">
                        <Zap className="w-4 h-4 flex-shrink-0" />
                        Hardware-optimized • Multi-GPU scaling • Production ready
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      </div>
    </header>
  );
}


const atlasIdeIconMap: Record<string, ElementType> = {
  atom: Atom,
  brain: Brain,
  rocket: Rocket,
  sparkles: Sparkles,
  workflow: Workflow,
};

function AtlasAI() {
  const atlasAIData = useContentSection("homepage.atlasAI") as Homepage["atlasAI"];

  return (
    <section className="section-primary">
      <h2 className="title-size font-semibold mb-1 flex justify-center items-center text-bg gap-2">
        {atlasAIData.title} -
        <span className="badge-size section-badge">{atlasAIData.subtitle}</span>
      </h2>
      <p className="text-size text-muted-foreground mb-18">{atlasAIData.description}</p>
      <article className="grid md:grid-cols-2 gap-10 items-center">
        <Image
          src={atlasAIData.image}
          alt="AtlasAI"
          width={1000}
          height={1000}
          className="w-full max-h-[500px]"
        />
        <div className="space-y-12 w-full text-[15px] lg:text-lg text-muted-foreground text-left">
          {atlasAIData.features.map((feature, index) => {
            const IconComponent = heroIconMap[feature.icon as keyof typeof heroIconMap];
            return (
              <div key={index} className="space-y-2 flex gap-2">
                <span className="iconBudg size-8 lg:size-10 mt-1">
                  <IconComponent className="size-5 lg:size-6" />
                </span>
                <p className="flex flex-col">
                  <span className="font-medium text-bg">{feature.title}</span> {feature.description}
                </p>
              </div>
            );
          })}

          <div className="flex justify-start gap-2 mt-10">
            <Link href={atlasAIData.button.link}>
              <Button variant={atlasAIData.button.variant} size={atlasAIData.button.size as "xl"}>
                {atlasAIData.button.text}
              </Button>
            </Link>
          </div>
        </div>
      </article>
    </section>
  );
}

function AtlasIDE() {
  const atlasIDEData = useContentSection("homepage.atlasIDE") as Homepage["atlasIDE"];

  return (
    <section className="section-primary">
      <p className="w-fit mx-auto badge-size section-badge mb-2">{atlasIDEData.badge}</p>
      <h1 className="title-size font-semibold mb-4 max-w-screen-md mx-auto text-bg">{atlasIDEData.subtitle}</h1>
      <p className="text-size text-muted-foreground mb-20">{atlasIDEData.description}</p>
      <article className="flex flex-col-reverse md:grid md:grid-cols-2 gap-10 items-center">
        <div className="space-y-12 w-full text-[15px] lg:text-lg text-muted-foreground text-left">
          {atlasIDEData.features.map((feature, index) => {
            const IconComponent = atlasIdeIconMap[feature.icon as keyof typeof atlasIdeIconMap];
            return (
              <div key={index} className="space-y-2 flex gap-2">
                <span className="iconBudg size-8 min-w-8 lg:size-10 lg:min-w-10 mt-1">
                  <IconComponent className="size-5 lg:size-6" />
                </span>
                <p className="flex flex-col">
                  <span className="font-medium text-bg">{feature.title}</span> {feature.description}
                </p>
              </div>
            );
          })}

          <div className="flex justify-start">
            <Link href={atlasIDEData.button.link}>
              <Button variant={atlasIDEData.button.variant} size={atlasIDEData.button.size as "xl"}>
                {atlasIDEData.button.text}
              </Button>
            </Link>
          </div>
        </div>
        <Image src={atlasIDEData.image} alt="AtlasIDE" width={1000} height={1000} className="w-full" />
      </article>
    </section>
  );
}

const technicalStackIconMap = {
  cpu: Cpu,
  code: Code,
  gauge: Gauge,
  gitBranch: GitBranch,
  server: Server,
  zap: Zap,
  database: Database,
  network: Network,
  cloud: Cloud,
  sparkles: Sparkles,
} satisfies Record<string, ElementType>;

type TechnicalStackIconKey = keyof typeof technicalStackIconMap;

function TechnicalStack() {
  const stackData = useContentSection("homepage.technicalStack") as Homepage["technicalStack"];

  if (!stackData?.tabs?.length) {
    return null;
  }

  return (
    <section className="section-technical-exp">
      <div className="text-center max-w-3xl mx-auto space-y-4">
        <p className="w-fit mx-auto badge-size section-badge">{stackData.badge}</p>
        <h2 className="title-size font-semibold text-bg">{stackData.title}</h2>
        <p className="text-size text-muted-foreground">{stackData.description}</p>
      </div>

      <Tabs defaultValue={stackData.tabs[0]?.id} className="max-w-6xl mx-auto mt-12 flex flex-col gap-10">
        <TabsList className="mx-auto flex-wrap bg-transparent gap-2 p-0">
          {stackData.tabs.map((tab) => {
            const Icon = technicalStackIconMap[(tab.icon as TechnicalStackIconKey) ?? "sparkles"] ?? Sparkles;
            return (
              <TabsTrigger
                key={tab.id}
                value={tab.id}
                className="flex items-center gap-2 border border-primary-100/60 bg-white/80 px-4 py-2 text-sm md:text-base md:px-5 md:py-3"
              >
                <Icon className="size-4 text-primary-600" />
                {tab.label}
              </TabsTrigger>
            );
          })}
        </TabsList>

        {stackData.tabs.map((tab) => (
          <TabsContent key={tab.id} value={tab.id} className="space-y-10">
            <p className="text-base md:text-lg text-muted-foreground text-center max-w-3xl mx-auto">{tab.summary}</p>
            <div className="grid gap-6 md:grid-cols-2">
              {tab.sections.map((section, index) => {
                const SectionIcon = technicalStackIconMap[(section.icon as TechnicalStackIconKey) ?? "sparkles"] ?? Sparkles;
                return (
                  <div
                    key={`${section.title}-${index}`}
                    className="rounded-3xl border border-primary-100 bg-white/90 p-6 shadow-sm"
                  >
                    <div className="flex items-center gap-2 mb-4">
                      <span className="iconBudg size-9">
                        <SectionIcon className="size-5" />
                      </span>
                      <h3 className="text-lg font-semibold text-bg">{section.title}</h3>
                    </div>
                    {section.type === "pills" ? (
                      <div className="flex flex-wrap gap-2">
                        {section.items.map((item) => (
                          <span
                            key={item}
                            className="rounded-full border border-primary-200 bg-primary-50 px-3 py-1 text-sm font-medium text-primary-700"
                          >
                            {item}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <ul className="space-y-3 text-sm md:text-base text-muted-foreground">
                        {section.items.map((item) => (
                          <li key={item} className="flex items-start gap-2">
                            <CheckCircle2 className="size-4 mt-1 text-primary-500 flex-shrink-0" />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                );
              })}
            </div>

            {tab.metrics?.length ? (
              <div className="grid gap-4 md:grid-cols-3">
                {tab.metrics.map((metric) => (
                  <div
                    key={metric.label}
                    className="rounded-2xl border border-primary-100 bg-gradient-to-br from-primary-100/60 to-white/90 p-6 text-center"
                  >
                    <div className="text-3xl font-semibold text-primary-700">{metric.value}</div>
                    <div className="mt-1 text-sm font-medium text-bg">{metric.label}</div>
                    {metric.description ? (
                      <p className="mt-2 text-xs text-muted-foreground">{metric.description}</p>
                    ) : null}
                  </div>
                ))}
              </div>
            ) : null}
          </TabsContent>
        ))}
      </Tabs>

      {stackData.hardware ? (
        <div className="max-w-5xl mx-auto mt-16 space-y-6">
          <div className="text-center space-y-2">
            <h3 className="text-2xl font-semibold text-bg">{stackData.hardware.title}</h3>
            <p className="text-muted-foreground text-base">{stackData.hardware.description}</p>
          </div>
          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
            {stackData.hardware.vendors.map((vendor) => (
              <div key={vendor.name} className="rounded-2xl border border-primary-100 bg-white/90 p-5 text-left">
                <div className="text-lg font-semibold text-bg">{vendor.name}</div>
                <div className="mt-2 text-sm font-medium text-primary-700">{vendor.models}</div>
                <p className="mt-2 text-xs text-muted-foreground leading-relaxed">{vendor.notes}</p>
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </section>
  );
}

function Marketplace() {
  const marketplaceData = useContentSection("homepage.marketplace") as Homepage["marketplace"];

  const getIconForPoint = (index: number) => {
    const icons = [CheckCircle2, Zap];
    const IconComponent = icons[index % icons.length];
    return IconComponent;
  };

  return (
    <section className="container max-w-screen-xl mx-auto py-0 grid md:grid-cols-2 gap-10 items-center">
      <Image
        src={marketplaceData.image}
        alt="Marketplace"
        width={1000}
        height={1000}
        className="w-full max-w-[800px] mx-auto"
      />
      <article className="space-y-10 md:space-y-16 text-left">
        <h2 className="title-size font-semibold text-bg mb-6">{marketplaceData.subtitle}</h2>

        <div className="space-y-4">
          {marketplaceData.points.map((point, index) => {
            const IconComponent = getIconForPoint(index);
            return (
              <div key={point} className="group flex items-start gap-4">
                <div className="flex-shrink-0 mt-0.5">
                  <IconComponent className="w-6 h-6 text-primary-600 group-hover:text-primary-700 transition-colors duration-300" />
                </div>
                <p className="text-[15px] lg:text-lg text-gray-800 group-hover:text-gray-900 transition-colors duration-300 leading-relaxed">
                  {point}
                </p>
              </div>
            );
          })}
        </div>

        {marketplaceData.stats?.length ? (
          <div className="grid gap-4 sm:grid-cols-3 py-4">
            {marketplaceData.stats.map((stat, index) => (
              <div
                key={`${stat.label}-${index}`}
                className="rounded-2xl border border-primary-100 bg-gradient-to-br from-primary-50 via-white to-white p-4 text-center shadow-sm"
              >
                <div className="text-2xl font-semibold text-primary-700">{stat.value}</div>
                <p className="text-sm font-medium text-bg">{stat.label}</p>
              </div>
            ))}
          </div>
        ) : null}

        <div className="flex justify-start gap-2">
          <Link href={marketplaceData.button.link}>
            <Button variant={marketplaceData.button.variant} size={marketplaceData.button.size as "xl"}>
              {marketplaceData.button.text}
            </Button>
          </Link>
        </div>
      </article>
    </section>
  );
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function Partners() {
  const partnersData = useContentSection("homepage.partners") as Homepage["partners"];
  const [hovered, setHovered] = useState<string | null>(null);
  const partners = partnersData.items;

  return (
    <section
      className="bg-muted/40 -mx-4 sm:-mx-8 px-4 sm:px-8 py-0 mb-10"
      onMouseLeave={() => setHovered(null)}
    >
      <div className="max-w-4xl mx-auto text-center space-y-3">
        <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
          {partnersData.kicker}
        </p>
        <h2 className="text-2xl font-semibold text-foreground">
          {partnersData.title}
        </h2>
        <p className="text-base text-muted-foreground">
          {partnersData.description}
        </p>
      </div>
      <div className="mt-8 flex flex-wrap items-center justify-center gap-6 text-muted-foreground/80">
        {partners.map((partner, idx) => {
          const key = `${partner.name}-${idx}` as const;

          return (
            <Link
              key={key}
              href={partner.href}
              id={`frontier_${partner.name}`}
              target="_blank"
              rel="noopener noreferrer"
              className={cn(
                "relative z-10 flex items-center justify-center rounded-lg border border-transparent",
                "px-4 py-3 transition-colors duration-200",
                hovered === key ? "border-primary-200 bg-primary-50/60" : "hover:border-muted-foreground/30 hover:bg-white"
              )}
              onMouseEnter={() => setHovered(key)}
            >
              {partner.logo ? (
                <Image
                  alt={partner.alt}
                  width={160}
                  height={48}
                  src={partner.logo}
                  className="max-h-10 w-auto object-contain opacity-80"
                  loading="lazy"
                />
              ) : (
                <span className="text-sm font-medium tracking-wide">{partner.name}</span>
              )}
            </Link>
          );
        })}
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <BaseLayout>
      <Hero />
      <TechnicalStack />
      <Marketplace />
      <AtlasAI />
      <AtlasIDE />
      {/* <Partners /> */}
    </BaseLayout>
  );
}